import { z } from 'zod'
import {
  DealSchema,
  DealSchemaForDMS,
  DealUsershipSchema,
  DealInviteSchema,
  ItemSchema,
  ActionItemSchema,
  ActionItemStatusSchema,
  CategorySchema,
  MarkupSchema,
  BlacklineSchema,
  ExportRequestSchema,
  SendDocumentsRequestSchema,
  VersionSchema,
  VersionMinimalSchema,
  RedactedUsershipSchema,
  DealVariableSchema,
  ItemWithVariableSchema,
  ItemVariableSchema,
  ScheduleSchema,
  SignatureSchema,
  SignaturePageSchema,
  SignaturePageTemplateSchema,
  DetectedSignatureSchema,
  AddSignaturesRequestSchema,
  AddSignaturesResponseSchema,
  EntitySchema,
  IndividualSchema,
  SigningPositionSchema,
  RedactedItemSchema,
  RedactedActionItemSchema,
  RedactedCategorySchema,
  RenderedVersionSchema,
  UserSchema,
  DealFieldListResponseSchema,
  ClosingVersionSchema,
  UploadSchema,
  UploadResponseSchema,
  FileObjectsResponseSchema,
  BulkCreateIndividualsResponseSchema,
  BulkCreateEntitiesResponseSchema,
  SyncSigningPackageResponseSchema,
  PerSignatorySendSchema,
  CreateSigningPackageSetResponseSchema,
  CreatePerSignatorySendResponseSchema,
} from '../schemas'
import { http } from './http'
import { auth } from './auth'
import {
  DealUsership,
  Region,
  DMSDocument,
  Role,
  DealInvite,
  DealCreate,
  SaveAsTemplatePayload,
  SetAgendaFirmsPayload,
  SetItemsCtypePayload,
  AgendaObjectConversionPayload,
  BulkCompileItem,
  UpdateActionItemStatusesPayload,
  DeleteActionItemStatusesPayload,
  BulkCreateIndividualsRequest,
  BulkCreateEntitiesRequest,
  Deal,
  SigningPackageCompilationRequest,
  SigningPackageCompilationService,
} from '../types'
import { parseWithZod } from '../utils/parse-with-zod'
import { serverVariables } from '../lib'

const DEALS_PER_REQUEST = 100

type DMSDealForm = {
  client_number: string
  closing_date: string | null
  deal_status_id: number | null
  matter_number: string
  title: string
  responsible_lawyer_id?: number | null
  deal_field_set_values?: {
    deal_field_set_id?: number
    field_values: { deal_field_id: number; value: string | null }[]
  }[]
}

type BulkUploadFromDMSRequest = {
  documents: DMSDocument[]
  should_match: boolean
  library_id: string | null
}

type BulkUploadFromDMSRefRequest = {
  documents: { library_id: string; document_id: number; version: number }[]
  should_match: boolean
}

type DMSFilePickerPayload =
  | { mode: 'open'; file_type_filter?: string[] }
  | { mode: 'browse' }
  | { mode: 'save'; filename: string; extension: string }

type approvalRuleState = 'disabled' | 'enabled'

const UpdateDealResponseSchema = z.object({
  deal_usership: z.object({
    id: z.number(),
    is_trashed: z.boolean(),
  }),
})

type UpdateDealResponse = z.infer<typeof UpdateDealResponseSchema>

const NewDealResponseSchema = z.object({
  deal: DealSchema,
})

export const DealInviteResponseSchema = z.object({
  deal_invite: DealInviteSchema,
})

const DealDetailsResponseSchema = z.object({
  deal: DealSchema,
})

export const BulkInviteEmailResponseSchema = z.object({
  task_id: z.string(),
  rejected_invites: z
    .array(
      z.object({
        email: z.string(),
        errors: z.object({ email: z.array(z.string()) }),
      }),
    )
    .optional(),
})

export type BulkInviteEmailResponse = z.infer<typeof BulkInviteEmailResponseSchema>

export const DealUsershipsResponseSchema = z.object({
  deal_userships: z.array(DealUsershipSchema),
})

const RedactedUsershipsResponseSchema = z.object({
  deal_userships: z.array(RedactedUsershipSchema),
})

const UsersNotAllowedResponseSchema = z.object({
  deal_usership_ids: z.array(z.number()),
  deal_invite_ids: z.array(z.number()),
})

const DealUsershipResponseSchema = z.object({
  deal_usership: DealUsershipSchema,
})

const DealInvitesResponseSchema = z.object({
  deal_invites: z.array(DealInviteSchema),
})

const DealListItemResponseSchema = DealSchema.pick({
  client_number: true,
  closing_date: true,
  creation_status: true,
  email: true,
  feature_flags: true,
  id: true,
  is_template: true,
  matter_number: true,
  organization_id: true,
  owner_firm_id: true,
  region_id: true,
  title: true,
  responsible_lawyer_id: true,
  deal_status_id: true,
  deal_status_updated_at: true,
  has_external_users: true,
  is_org_wide: true,
  owner_organization: true,
  created_by_name: true,
  created_by_email: true,
  created_by: true,
})

const DealListResponseSchema = z.object({
  deals: z.array(DealListItemResponseSchema),
})

const NewDMSDealResponseSchema = z.object({
  deal: DealListItemResponseSchema.omit({
    deal_status_updated_at: true,
  }),
})

const DealListSchemaForDMS = z.object({
  deals: z.array(DealSchemaForDMS),
  region_id: z.number(),
})

const BulkUploadFromDMSResponseSchema = z.object({
  uploads: z.array(UploadSchema),
})

const BulkUploadFromDMSRefResponseSchema = z.object({
  uploads: z.array(UploadSchema),
  failed: z.array(z.unknown()).optional(),
})

const DMSFilePickerURLResponseSchema = z.object({
  file_picker_url: z.string(),
})

const DealVariablesResponseSchema = z.object({
  deal_variables: z.array(DealVariableSchema),
})

const SetDealVariableValueResponseSchema = z.object({
  deal_variable: DealVariableSchema,
  item_variables: z.array(ItemVariableSchema),
})

const ItemWithVariablesResponseSchema = z.object({
  item_with_variables: z.array(ItemWithVariableSchema),
})

export const VersionResponseSchema = z.object({
  versions: z.array(VersionSchema),
})

export const VersionMinimalResponseSchema = z.object({
  versions: z.array(VersionMinimalSchema),
})

export const ItemsResponseSchema = z.object({
  items: z.array(ItemSchema),
})

export const AgendaObjectConversionResponseSchema = z.object({
  job_id: z.string(),
})

export const ActionItemsResponseSchema = z.object({
  action_items: z.array(ActionItemSchema),
})

const ActionItemStatusesResponseSchema = z.object({
  action_item_statuses: z.array(ActionItemStatusSchema),
})

const MarkupsResponseSchema = z.object({
  markups: z.array(MarkupSchema),
})

const BlacklinesResponseSchema = z.object({
  blacklines: z.array(BlacklineSchema),
})

const ClosingVersionsResponseSchema = z.object({
  closing_versions: z.array(ClosingVersionSchema),
})

export const CategoriesResponseSchema = z.object({
  categories: z.array(CategorySchema),
})

const BulkScheduleDeletionResponseSchema = z.object({
  deals: z.array(
    z.object({
      id: z.number(),
      deletion_date: z.string().nullable(),
    }),
  ),
})

const ApprovalRuleResponseSchema = z.object({
  deal: DealSchema,
})

const AccessRuleResponseSchema = z.object({
  deal: DealSchema,
})

const RenderAllResponseSchema = z.object({
  success_count: z.number(),
  conflicts: z.array(
    z.object({
      reason: z.enum(['already_rendering', 'signature_pages_not_ready']),
    }),
  ),
})
const ItemVariableResponseSchema = z.object({ item_variables: z.array(ItemVariableSchema) })

const InviteCandidateResponseSchema = z.object({
  users: z.array(
    UserSchema.pick({
      id: true,
      email: true,
      first_name: true,
      last_name: true,
      organization_id: true,
      state: true,
    }),
  ),
})

const InviteResponseSchema = z.union([DealUsershipResponseSchema, DealInviteResponseSchema])

const IndividualsResponseSchema = z.object({
  individuals: z.array(IndividualSchema),
})

const EntitiesResponseSchema = z.object({
  entities: z.array(EntitySchema),
})

const SigningPositionsResponseSchema = z.object({
  signing_positions: z.array(SigningPositionSchema),
})

const DetectedSignaturesResponseSchema = z.object({
  detected_signatures: z.array(DetectedSignatureSchema),
})

const RenderedVersionsResponseSchema = z.object({
  rendered_versions: z.array(RenderedVersionSchema),
})

// A 200 can still come back with no URL, so this is nullish rather than required.
const DocusignSenderUrlResponseSchema = z.object({
  sender_url: z.string().nullish(),
})

const CompileFailureSchema = z.object({
  reason: z.string(),
  item_ids: z.array(z.number()),
})

const CompileFailuresSchema = z
  .union([CompileFailureSchema, z.array(CompileFailureSchema)])
  .transform(failed => (Array.isArray(failed) ? failed : [failed]))

const BulkQuickCompileResponseSchema = z.object({
  item_ids: z.array(z.number()),
  failed: CompileFailuresSchema,
  closing_versions: z.array(
    ClosingVersionSchema.extend({ document: ClosingVersionSchema.shape.document.nullable() }),
  ),
})

const BulkMarkFinalResponseSchema = z.object({
  success_count: z.number(),
  failed: z.array(
    z.object({
      reason: z.string(),
      version_ids: z.array(z.number()),
    }),
  ),
})

const BulkCompileResponseSchema = z.object({
  failed: CompileFailuresSchema,
  closing_versions: z.array(
    ClosingVersionSchema.extend({ document: ClosingVersionSchema.shape.document.nullable() }),
  ),
})

const chunkArray = <T>(array: T[], batchSize: number): T[][] => {
  const batches: T[][] = []
  for (let i = 0; i < array.length; i += batchSize) {
    batches.push(array.slice(i, i + batchSize))
  }
  return batches
}

const chunkUrlRequests = (dealIds: number[], host: string): string[] => {
  const urls: string[] = []

  chunkArray(dealIds, DEALS_PER_REQUEST).map(ids => {
    const query = new URLSearchParams()
    ids.forEach(id => query.append('ids[]', id.toString()))
    const url = `${window.location.protocol}//${host}/api/deals?${query}`
    urls.push(url)
  })

  return urls
}

export const deals = {
  heartbeat: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    await http.post(`${regionHost}/api/deals/${dealId}/heartbeat`, undefined, dealId)
  },

  create: async (newDeal: DealCreate) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(`${regionHost}/api/deals`, { deal: newDeal })

    const parsedResult = parseWithZod(NewDealResponseSchema, result)
    return parsedResult.deal
  },

  clone: async (
    sourceDealId: number,
    cloneDeal: DealCreate,
    options?: { useTemplateEndpoint?: boolean },
  ) => {
    const regionHost = auth.getUserRegionHost()
    const endpoint = options?.useTemplateEndpoint
      ? `${regionHost}/api/deal_templates/${sourceDealId}/clone`
      : `${regionHost}/api/deals/${sourceDealId}/clone`
    const result = await http.post(endpoint, {
      deal: cloneDeal,
    })

    return result
  },

  saveAsTemplate: async (sourceDealId: number, payload: SaveAsTemplatePayload) => {
    const regionHost = auth.getUserRegionHost()
    await http.post(`${regionHost}/api/deals/${sourceDealId}/save_as_template`, payload)
  },

  createForDMS: async (deal: DMSDealForm) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(`${regionHost}/api/deals/create_for_dms`, {
      deal,
    })

    const parsedResult = parseWithZod(NewDMSDealResponseSchema, result)
    return parsedResult.deal
  },

  getUserships: async (userId: number, regionHost?: string) => {
    const host = regionHost ? `${location.protocol}//${regionHost}` : auth.getUserRegionHost()
    const result = await http.get(`${host}/api/users/${userId}/deal_userships`)
    return parseWithZod(DealUsershipsResponseSchema, result)
  },

  getUsershipsByDeal: async (dealId: number, userId?: number) => {
    const regionHost = auth.getUserRegionHost()
    let url
    if (userId) {
      url = `${regionHost}/api/deals/${dealId}/userships?user_id=${userId}`
    } else {
      url = `${regionHost}/api/deals/${dealId}/userships`
    }
    const result = await http.get(url, undefined, dealId)
    const parsedResult = parseWithZod(DealUsershipsResponseSchema, result)
    return parsedResult.deal_userships
  },

  getRedactedUserships: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/deals/${dealId}/redacted_userships`,
      undefined,
      dealId,
    )
    const parsedResult = parseWithZod(RedactedUsershipsResponseSchema, result)
    return parsedResult.deal_userships
  },

  getUsersNotAllowed: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/deals/${dealId}/users_not_allowed`,
      undefined,
      dealId,
    )
    return parseWithZod(UsersNotAllowedResponseSchema, result)
  },

  deleteUsership: async (userId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    return await http.delete(`${regionHost}/api/deal_userships/${userId}`, dealId)
  },

  getDealInvites: async (orgId: number, email: string) => {
    const regionHost = auth.getUserRegionHost()
    const encodedEmail = encodeURIComponent(email)
    const result = await http.get(
      `${regionHost}/api/organizations/${orgId}/deal_invites?email=${encodedEmail}`,
    )
    return parseWithZod(DealInvitesResponseSchema, result)
  },

  updateDealInvite: async (dealInvite: DealInvite, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const response = await http.put(
      `${regionHost}/api/deal_invites/${dealInvite.id}`,
      {
        deal_invite: dealInvite,
      },
      dealId,
    )
    return parseWithZod(DealInviteResponseSchema, response)
  },

  resendDealInvite: async (dealInviteId: number) => {
    const regionHost = auth.getUserRegionHost()
    const response = await http.post(
      `${regionHost}/api/deal_invites/${dealInviteId}/resend_activation`,
      undefined,
      serverVariables.dealId ?? undefined,
    )
    const parsedResult = parseWithZod(DealInviteResponseSchema, response)
    return parsedResult.deal_invite
  },

  deleteDealInvite: async (dealInviteId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    return await http.delete(`${regionHost}/api/deal_invites/${dealInviteId}`, dealId)
  },

  changeUsershipRole: async (userId: number, role: Role, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const response = await http.post(
      `${regionHost}/api/deal_userships/${userId}/change_role`,
      {
        role,
      },
      dealId,
    )
    return parseWithZod(DealUsershipResponseSchema, response)
  },

  getAll: async (regionDealIds: { region: Region; dealIds: number[] }[]) => {
    const dealPromises = regionDealIds.map(({ region, dealIds }) =>
      fetchRegionDeals(region, dealIds),
    )
    const dealArrays = await Promise.all(dealPromises)
    return dealArrays.flat()
  },

  getAllByIds: async (regions: Region[], dealIds: number[]) => {
    const dealIdBatches = chunkArray(dealIds, 100)
    const allDeals = await Promise.all(
      regions.flatMap(region => dealIdBatches.map(batch => fetchRegionDealsByIds(region, batch))),
    )
    return allDeals.flat()
  },

  getAllForDMSUpload: async (clientId: string, customerId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/deals/deals_for_dms_upload`, {
      client_id: clientId,
      customer_id: customerId,
    })
    return parseWithZod(DealListSchemaForDMS, result)
  },

  bulkUploadFromDMS: async (payload: BulkUploadFromDMSRequest, deal_id: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/deals/${deal_id}/bulk_upload_from_dms`,
      payload,
    )
    return parseWithZod(BulkUploadFromDMSResponseSchema, result)
  },

  bulkUploadFromDMSRef: async (payload: BulkUploadFromDMSRefRequest, deal_id: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/deals/${deal_id}/bulk_upload_from_dms_ref`,
      payload,
      deal_id,
    )
    return parseWithZod(BulkUploadFromDMSRefResponseSchema, result)
  },

  updateDeal: async ({
    usership_id,
    is_being_archived,
    deal_id,
    regionHost,
  }: {
    usership_id: number
    is_being_archived: boolean
    deal_id: number
    regionHost?: string
  }): Promise<UpdateDealResponse> => {
    const host = regionHost ? `${location.protocol}//${regionHost}` : auth.getUserRegionHost()
    const endpoint = `${host}/api/deal_userships/${usership_id}/${is_being_archived ? 'archive' : 'unarchive'}`
    const result = await http.post(endpoint, undefined, deal_id)
    return parseWithZod(UpdateDealResponseSchema, result)
  },

  setCreationStatus: async (
    deal: { id: number; title: string; responsible_lawyer_id?: number | null },
    creationStatus: 'new' | 'importing' | 'ready',
  ) => {
    const regionHost = auth.getUserRegionHost()
    await http.put(
      `${regionHost}/api/deals/${deal.id}`,
      {
        deal: {
          creation_status: creationStatus,
          title: deal.title,
          responsible_lawyer_id: deal.responsible_lawyer_id ?? null,
        },
      },
      deal.id,
    )
  },

  requestImport: async (dealId: number, payload: { upload_id: number; comments: string }) => {
    const regionHost = auth.getUserRegionHost()
    await http.post(`${regionHost}/api/deals/${dealId}/request_import`, payload, dealId)
  },

  getById: async (dealId: number, host?: string) => {
    const regionHost = host ? `${location.protocol}//${host}` : auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/deals/${dealId}`)
    const parsedResult = parseWithZod(DealDetailsResponseSchema, result)
    return parsedResult.deal
  },

  getDealFields: async (dealId: number, host?: string) => {
    const regionHost = host ? `${location.protocol}//${host}` : auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/deals/${dealId}/deal_fields`)
    return parseWithZod(DealFieldListResponseSchema, result).deal_fields
  },

  getDMSFilePickerURL: async (dealId: number, payload: DMSFilePickerPayload) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(`${regionHost}/api/deals/${dealId}/dms_file_picker_url`, payload)
    return parseWithZod(DMSFilePickerURLResponseSchema, result)
  },

  editInfo: async (payload: {
    id: number
    title: string
    closing_date: string | null
    client_number: string
    matter_number: string
    deal_status_id: number | null
    responsible_lawyer_id: number | null
    preamble: string | null
    always_show_overview: boolean
    is_org_wide?: boolean
    deal_field_set_values?: {
      deal_field_set_id: number
      field_values: { deal_field_id: number; value: string | null }[]
    }[]
    host?: string
  }) => {
    const { id, host, ...restOfPayload } = payload
    const regionHost = host ? `${location.protocol}//${host}` : auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/deals/${id}/edit_deal_information`,
      restOfPayload,
    )
    return parseWithZod(DealDetailsResponseSchema, result).deal
  },

  getItems: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/deals/${dealId}/items`)
    const parsedResult = parseWithZod(ItemsResponseSchema, result)
    return parsedResult.items
  },

  getRedactedItems: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/deals/${dealId}/redacted_items`)
    const parsedResult = parseWithZod(
      z.object({
        items: z.array(RedactedItemSchema),
      }),
      result,
    )
    return parsedResult.items
  },

  getVersions: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/deals/${dealId}/versions`)
    const parsedResult = parseWithZod(VersionResponseSchema, result)
    return parsedResult.versions
  },

  getVersionsMinimal: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/deals/${dealId}/versions_minimal`)
    const parsedResult = parseWithZod(VersionMinimalResponseSchema, result)
    return parsedResult.versions
  },

  getActionItems: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/deals/${dealId}/action_items`)
    const parsedResult = parseWithZod(ActionItemsResponseSchema, result)
    return parsedResult.action_items
  },

  getRedactedActionItems: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/deals/${dealId}/redacted_action_items`)
    const parsedResult = parseWithZod(
      z.object({
        action_items: z.array(RedactedActionItemSchema),
      }),
      result,
    )
    return parsedResult.action_items
  },

  getActionItemStatuses: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/deals/${dealId}/action_item_statuses`)
    const parsedResult = parseWithZod(ActionItemStatusesResponseSchema, result)
    return parsedResult.action_item_statuses
  },

  updateActionItemStatuses: async (dealId: number, payload: UpdateActionItemStatusesPayload) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/deals/${dealId}/modify_action_item_statuses`,
      payload,
      dealId,
    )
    const parsedResult = parseWithZod(ActionItemStatusesResponseSchema, result)
    return parsedResult.action_item_statuses
  },

  deleteActionItemStatuses: async (dealId: number, payload: DeleteActionItemStatusesPayload) => {
    const regionHost = auth.getUserRegionHost()
    await http.post(
      `${regionHost}/api/deals/${dealId}/delete_action_item_statuses`,
      payload,
      dealId,
    )
  },

  getCategories: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/deals/${dealId}/categories`)
    const parsedResult = parseWithZod(CategoriesResponseSchema, result)
    return parsedResult.categories
  },

  getMarkups: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/deals/${dealId}/markups`)
    const parsedResult = parseWithZod(MarkupsResponseSchema, result)
    return parsedResult.markups
  },

  getClosingVersions: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/deals/${dealId}/closing_versions`)
    const parsedResult = parseWithZod(ClosingVersionsResponseSchema, result)
    return parsedResult.closing_versions
  },

  getBlacklines: async (dealId: number, contentType: 'version' | 'snapshot') => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/deals/${dealId}/blacklines?content_type=${contentType}`,
    )
    const parsedResult = parseWithZod(BlacklinesResponseSchema, result)
    return parsedResult.blacklines
  },

  exportItems: async (dealId: number, payload: z.infer<typeof ExportRequestSchema>) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(`${regionHost}/api/deals/${dealId}/export`, payload, dealId)
    const parsedResult = parseWithZod(UploadResponseSchema, result)
    return parsedResult.upload
  },

  exportSigningMatrix: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/deals/${dealId}/export_signing_matrix`,
      undefined,
      dealId,
    )
    const parsedResult = parseWithZod(UploadResponseSchema, result)
    return parsedResult.upload
  },

  syncSigningPackage: async (dealId: number, signingPackageToIndividualId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/deals/${dealId}/sync_signing_package`,
      { signing_package_to_individual_id: signingPackageToIndividualId },
      dealId,
    )
    const parsedResult = parseWithZod(SyncSigningPackageResponseSchema, result)
    return parsedResult.individual
  },

  /**
   * Starts an async compile; the result arrives later as a `{service}-success` / `-failure` Pusher
   * push carrying the returned set id.
   */
  createSigningPackageSet: async (
    dealId: number,
    service: SigningPackageCompilationService,
    payload: SigningPackageCompilationRequest,
  ) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/deals/${dealId}/create_${service}_signing_package_set`,
      payload,
      dealId,
    )
    return parseWithZod(CreateSigningPackageSetResponseSchema, result).signing_package_set.id
  },

  /** One DocuSign envelope per signatory, grouped under a send the drafts page is keyed by. */
  createDocusignSigningPackageSetPerSignatory: async (
    dealId: number,
    payload: SigningPackageCompilationRequest,
  ) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/deals/${dealId}/create_docusign_signing_package_set_per_signatory`,
      payload,
      dealId,
    )
    return parseWithZod(CreatePerSignatorySendResponseSchema, result).per_signatory_send_id
  },

  sendDocuments: async (dealId: number, payload: z.infer<typeof SendDocumentsRequestSchema>) => {
    const regionHost = auth.getUserRegionHost()
    await http.post(`${regionHost}/api/deals/${dealId}/send_documents`, payload, dealId)
  },
  getRedactedCategories: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/deals/${dealId}/redacted_categories`)
    const parsedResult = parseWithZod(
      z.object({
        categories: z.array(RedactedCategorySchema),
      }),
      result,
    )
    return parsedResult.categories
  },

  getSchedules: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/deals/${dealId}/schedules`)
    const parsedResult = parseWithZod(z.object({ schedules: z.array(ScheduleSchema) }), result)
    return parsedResult.schedules
  },

  getSignatures: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/deals/${dealId}/signatures`)
    const parsedResult = parseWithZod(z.object({ signatures: z.array(SignatureSchema) }), result)
    return parsedResult.signatures
  },

  getIndividuals: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/deals/${dealId}/individuals`)
    const parsedResult = parseWithZod(IndividualsResponseSchema, result)
    return parsedResult.individuals
  },

  getEntities: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/deals/${dealId}/entities`)
    const parsedResult = parseWithZod(EntitiesResponseSchema, result)
    return parsedResult.entities
  },

  getSigningPositions: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/deals/${dealId}/signing_positions`)
    const parsedResult = parseWithZod(SigningPositionsResponseSchema, result)
    return parsedResult.signing_positions
  },

  getSignaturePages: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/deals/${dealId}/signature_pages`,
      undefined,
      dealId,
    )
    return parseWithZod(z.object({ signature_pages: z.array(SignaturePageSchema) }), result)
      .signature_pages
  },

  getDetectedSignatures: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/deals/${dealId}/detected_signatures`,
      undefined,
      dealId,
    )
    return parseWithZod(DetectedSignaturesResponseSchema, result).detected_signatures
  },

  getSignaturePageTemplates: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/deals/${dealId}/signature_page_templates`,
      undefined,
      dealId,
    )
    return parseWithZod(
      z.object({ signature_page_templates: z.array(SignaturePageTemplateSchema) }),
      result,
    ).signature_page_templates
  },

  updateSignaturePageDefaults: async (
    deal: Deal,
    values: { signature_page_lead_in_text: string; signature_page_template_id: number },
  ) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.put(
      `${regionHost}/api/deals/${deal.id}`,
      { deal: { ...deal, ...values } },
      deal.id,
    )
    return parseWithZod(DealDetailsResponseSchema, result).deal
  },

  updateSignatures: async (dealId: number, payload: z.infer<typeof AddSignaturesRequestSchema>) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/deals/${dealId}/update_signatures`,
      payload,
      dealId,
    )
    return parseWithZod(AddSignaturesResponseSchema, result)
  },

  bulkScheduleDeletion: async (organizationId: number, payload: { deal_ids: number[] }) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/organizations/${organizationId}/bulk_schedule_deal_deletion`,
      payload,
    )
    return parseWithZod(BulkScheduleDeletionResponseSchema, result)
  },

  bulkUnscheduleDeletion: async (organizationId: number, payload: { deal_ids: number[] }) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/organizations/${organizationId}/bulk_unschedule_deal_deletion`,
      payload,
    )
    return parseWithZod(BulkScheduleDeletionResponseSchema, result)
  },

  createSupportTicket: async (
    dealId: number,
    payload: { description: string; allow_access: boolean; url: string },
  ) => {
    const regionHost = auth.getUserRegionHost()
    await http.post(`${regionHost}/api/deals/${dealId}/create_support_ticket`, payload, dealId)
  },

  sendClosingBookFeedback: async (dealId: number, payload: { reason: string; feature: string }) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(`${regionHost}/api/deals/${dealId}/send_feedback`, payload)
    return result
  },

  inviteEmail: async (
    dealId: number,
    payload: {
      email: string
      firm_id: number
      role: string
      temporary_access?: boolean
    },
  ) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(`${regionHost}/api/deals/${dealId}/invite_email`, payload)
    return parseWithZod(InviteResponseSchema, result)
  },

  bulkInviteEmail: async (
    dealId: number,
    payload: {
      invites: Array<{
        email: string
        role: string
        firm_id: number
        temporary_access?: boolean
      }>
    },
  ) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(`${regionHost}/api/deals/${dealId}/bulk_invite_email`, payload)
    return parseWithZod(BulkInviteEmailResponseSchema, result)
  },

  getInviteCandidates: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/deals/${dealId}/invite_candidates`,
      undefined,
      dealId,
    )
    const parsedResult = parseWithZod(InviteCandidateResponseSchema, result)
    return parsedResult.users
  },

  updateAccessRules: async (
    dealId: number,
    payload: {
      default_access_rule: string
      variable_access_rule: string
      group_access_rule?: string
    },
  ) => {
    const regionHost = auth.getUserRegionHost()

    const result = await http.post(`${regionHost}/api/deals/${dealId}/update_access_rules`, payload)
    const parsedResult = AccessRuleResponseSchema.parse(result)
    return parsedResult.deal
  },

  updateApprovalRule: async (dealId: number, payload: { approval_rule: approvalRuleState }) => {
    const regionHost = auth.getUserRegionHost()

    const result = await http.post(
      `${regionHost}/api/deals/${dealId}/update_approval_rule`,
      payload,
    )
    const parsedResult = ApprovalRuleResponseSchema.parse(result)
    return parsedResult.deal
  },

  getDealVariables: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/deals/${dealId}/deal_variables`)
    const parsedResult = parseWithZod(DealVariablesResponseSchema, result)
    return parsedResult.deal_variables
  },

  updateDealVariable: async (variableId: number, value: string | null, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/deal_variables/${variableId}/set_value`,
      {
        value,
      },
      dealId,
    )
    const parsedResult = parseWithZod(SetDealVariableValueResponseSchema, result)
    return parsedResult
  },

  getItemWithVariables: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/deals/${dealId}/item_with_variables`)
    const parsedResult = parseWithZod(ItemWithVariablesResponseSchema, result)
    return parsedResult.item_with_variables
  },

  getRenderedVersions: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/deals/${dealId}/rendered_versions`,
      undefined,
      dealId,
    )
    const parsedResult = parseWithZod(RenderedVersionsResponseSchema, result)
    return parsedResult.rendered_versions
  },

  changeNotificationFrequency: async (usershipId: number, payload: { email_frequency: string }) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/deal_userships/${usershipId}/change_notification_frequency`,
      payload,
      serverVariables.dealId ?? undefined,
    )
    const parsedResult = parseWithZod(DealUsershipResponseSchema, result)
    return parsedResult.deal_usership
  },

  changeUsershipGroup: async (usershipId: number, payload: { firm_id: number }) => {
    const regionHost = auth.getUserRegionHost()
    const response = await http.post(
      `${regionHost}/api/deal_userships/${usershipId}/change_group`,
      payload,
      serverVariables.dealId ?? undefined,
    )
    const parsedResult = parseWithZod(DealUsershipResponseSchema, response)
    return parsedResult.deal_usership
  },

  enableAi: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(`${regionHost}/api/deals/${dealId}/enable_ai`, {}, dealId)
    const parsedResult = parseWithZod(DealDetailsResponseSchema, result)
    return parsedResult.deal
  },

  disableAi: async (dealId: number, payload: { reason?: string }) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(`${regionHost}/api/deals/${dealId}/disable_ai`, payload, dealId)
    const parsedResult = parseWithZod(DealDetailsResponseSchema, result)
    return parsedResult.deal
  },

  renderAllVariables: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(`${regionHost}/api/deals/${dealId}/render_all`, {}, dealId)
    return parseWithZod(RenderAllResponseSchema, result)
  },

  getItemVariables: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/deals/${dealId}/item_variables`,
      undefined,
      dealId,
    )
    const parsedResult = parseWithZod(ItemVariableResponseSchema, result)
    return parsedResult.item_variables
  },

  getFileObjects: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/deals/${dealId}/file_objects`)
    return parseWithZod(FileObjectsResponseSchema, result)
  },

  bulkQuickCompileClosingVersion: async (dealId: number, itemIds: number[]) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/deals/${dealId}/bulk_quick_compile_closing_version`,
      { item_ids: itemIds },
      dealId,
    )
    return parseWithZod(BulkQuickCompileResponseSchema, result)
  },

  bulkMarkFinal: async (dealId: number, versionIds: number[]) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/deals/${dealId}/bulk_mark_final`,
      { version_ids: versionIds },
      dealId,
    )
    return parseWithZod(BulkMarkFinalResponseSchema, result)
  },

  bulkCompileClosingVersion: async (dealId: number, items: BulkCompileItem[]) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/deals/${dealId}/bulk_compile_closing_version`,
      { items },
      dealId,
    )
    return parseWithZod(BulkCompileResponseSchema, result)
  },

  createAgendaPrint: async (dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    await http.post(`${regionHost}/api/deals/${dealId}/create_agenda_print`, undefined, dealId)
  },

  setAgendaFirms: async (dealId: number, payload: SetAgendaFirmsPayload) => {
    const regionHost = auth.getUserRegionHost()
    await http.post(`${regionHost}/api/deals/${dealId}/set_agenda_firms`, payload, dealId)
  },

  setItemsCtype: async (dealId: number, payload: SetItemsCtypePayload) => {
    const regionHost = auth.getUserRegionHost()
    const data = await http.post(
      `${regionHost}/api/deals/${dealId}/set_item_ctype`,
      payload,
      dealId,
    )
    return parseWithZod(ItemsResponseSchema, data).items
  },

  agendaObjectConversion: async (dealId: number, payload: AgendaObjectConversionPayload) => {
    const regionHost = auth.getUserRegionHost()
    const data = await http.post(
      `${regionHost}/api/deals/${dealId}/agenda_object_conversion`,
      payload,
      dealId,
    )
    return parseWithZod(AgendaObjectConversionResponseSchema, data)
  },

  bulkCreateIndividuals: async (dealId: number, payload: BulkCreateIndividualsRequest) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/deals/${dealId}/bulk_create_individuals`,
      payload,
      dealId,
    )
    return parseWithZod(BulkCreateIndividualsResponseSchema, result)
  },

  bulkCreateEntities: async (dealId: number, payload: BulkCreateEntitiesRequest) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/deals/${dealId}/bulk_create_entities`,
      payload,
      dealId,
    )
    return parseWithZod(BulkCreateEntitiesResponseSchema, result)
  },

  getPerSignatorySend: async (dealId: number, sendId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/deals/${dealId}/per_signatory_send/${sendId}`,
      undefined,
      dealId,
    )
    return parseWithZod(PerSignatorySendSchema, result)
  },

  /**
   * Mints a short-lived DocuSign sender URL for one envelope. Only needed for a `created` set —
   * every later status already carries its own `url`.
   */
  getDocusignSenderUrl: async (dealId: number, setId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/deals/${dealId}/docusign_sender_url/${setId}`,
      undefined,
      dealId,
    )
    return parseWithZod(DocusignSenderUrlResponseSchema, result).sender_url
  },
}

const fetchRegionDeals = async (region: Region, dealIds: number[]) => {
  if (dealIds.length === 0) return []

  const urls = chunkUrlRequests(dealIds, region.host)

  const responses = await Promise.all(
    urls.map(async url => {
      const response = await http.get(url)
      return parseWithZod(DealListResponseSchema, response)
    }),
  )

  return responses.flatMap(data =>
    data.deals.map(deal => ({ ...deal, region_host: region.host, region_id: region.id })),
  )
}

// Per-user fields come from the usership, so a usership update shows without refetching deals
export const joinDealUsership = (
  deal: RegionDeal,
  usership: DealUsership,
): DealListItemWithUsership => ({
  ...deal,
  usership_id: usership.id,
  is_trashed: usership.is_trashed,
  user_last_accessed: usership.last_access,
  tfa_required: usership.tfa_required,
  has_access: usership.has_access,
  deal_category_title: usership.deal_category_title,
})

const fetchRegionDealsByIds = async (region: Region, dealIds: number[]) => {
  if (dealIds.length === 0) {
    return []
  }
  const query = new URLSearchParams()
  dealIds.forEach(id => query.append('ids[]', id.toString()))
  const url = `${window.location.protocol}//${region.host}/api/deals?${query}`
  const response = await http.get(url)
  const data = parseWithZod(DealListResponseSchema, response)
  return data.deals
}

export type RegionDeal = z.infer<typeof DealListItemResponseSchema> & {
  region_host: string
  region_id: number
}

export type DealListItemWithUsership = RegionDeal & {
  usership_id: number
  is_trashed: boolean
  user_last_accessed: string | null
  tfa_required: boolean
  has_external_users?: boolean | null
  deal_template_id?: number
  has_access?: boolean
  deal_category_title?: string | null
}
